import { CardProps, Text } from "@chakra-ui/react";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  GetDashboardFormSchoolGradeRankResponse,
  GetGradeConfigByEnrollmentPeriodResponse,
  GetSchoolsByEnrollmentPeriodResponse,
} from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import * as Format from "src/services/format";
import { isNotNull } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { ChartTooltip } from "./ChartTooltip";
import { DashboardChart } from "./DashboardChart";
import { Rank, RankMultiSelectInput } from "./RankMultiSelectInput";
import { School, SchoolsMultiSelectInput } from "./SchoolsMultiSelectInput";

type Props = {
  exportId?: string;
  remoteData: [
    RD.RemoteData<unknown, GetDashboardFormSchoolGradeRankResponse>,
    RD.RemoteData<unknown, GetSchoolsByEnrollmentPeriodResponse>,
    RD.RemoteData<unknown, GetGradeConfigByEnrollmentPeriodResponse>
  ];
  cardProps?: CardProps;
  isSchoolRankingEnabled: boolean;
};
export const ResponsesByGrade: React.FC<Props> = ({
  exportId,
  remoteData: [formRD, schoolsRD, gradesRD],
  cardProps,
  isSchoolRankingEnabled,
}) => {
  const [selectedSchools, setSelectedSchools] = React.useState<School[] | null>(
    null
  );
  const [selectedRanks, setSelectedRanks] = React.useState<Rank[] | null>(null);

  const allSchools = React.useMemo(() => {
    if (schoolsRD.hasData()) {
      return schoolsRD.data.data;
    }
    return [];
  }, [schoolsRD]);

  const allRanksRD = React.useMemo(() => {
    return formRD.map((data) => {
      const uniqueRanks = new Set(data.data.map((item) => item.rank));
      return Array.from(uniqueRanks)
        .filter(isNotNull)
        .sort((a, b) => a - b)
        .map((rank) => ({
          id: String(rank),
          label: String(rank + 1),
        }));
    });
  }, [formRD]);

  const allRanks = React.useMemo(() => {
    return allRanksRD.withDefault([]);
  }, [allRanksRD]);

  const data = React.useMemo(() => {
    return formRD.map((formData) =>
      transformData(formData, selectedSchools, selectedRanks)
    );
  }, [formRD, selectedSchools, selectedRanks]);

  const { glossary } = useGlossary();

  return (
    <DashboardChart
      aspectRatio={1.5}
      cardProps={cardProps}
      title="Responses by grade"
      caption={glossary`Form responses by grade, not unique by student`}
      data={data}
      exportId={exportId}
      exportFileName="responses-by-grade"
      filters={
        <>
          <SchoolsMultiSelectInput
            remoteData={schoolsRD}
            onChange={setSelectedSchools}
            selectedSchools={selectedSchools ?? allSchools}
          />
          {isSchoolRankingEnabled ? (
            <RankMultiSelectInput
              remoteData={allRanksRD}
              onChange={setSelectedRanks}
              selectedRanks={selectedRanks ?? allRanks}
            />
          ) : null}
        </>
      }
    >
      {(data) => (
        <BarChart
          style={{
            width: "100%",
            aspectRatio: 1.5,
          }}
          responsive
          layout="vertical"
          data={data}
          margin={{
            top: 0,
            right: 24,
            left: 24,
            bottom: 5,
          }}
        >
          <defs>
            <linearGradient id="gradeBarFill" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#7A57D1" />
              <stop offset="100%" stopColor="#845CD9" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical />
          <XAxis
            type="number"
            orientation="top"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            tickLine={false}
            axisLine={true}
            fontSize="0.75rem"
            interval={0}
          />
          <Tooltip<number, string>
            content={({ active, payload, label }) => {
              const value = payload[0]?.payload?.value;
              if (active && payload && value) {
                return (
                  <ChartTooltip
                    accentColor="#845CD9"
                    title={String(label ?? "")}
                  >
                    <Text fontSize="0.9rem">
                      <strong>{Format.formatWholeNumber(value)}</strong>{" "}
                      {value === 1 ? "response" : "responses"}
                    </Text>
                  </ChartTooltip>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="value" fill="url(#gradeBarFill)" barSize={30}>
            <LabelList dataKey="value" position="right" fill="#845CD9" />
          </Bar>
        </BarChart>
      )}
    </DashboardChart>
  );
};

export function transformData(
  formData: GetDashboardFormSchoolGradeRankResponse,
  selectedSchools: School[] | null,
  selectedRanks: Rank[] | null
): { name: string; value: number }[] {
  const gradeData = new Map<string, { label: string; count: number }>();

  for (const row of formData.data) {
    // null means "all selected" — skip that filter entirely
    if (selectedSchools !== null) {
      if (selectedSchools.length === 0) {
        // empty array means no schools selected — only include rows without a school
        if (row.school_id !== null) continue;
      } else if (!selectedSchools.find((s) => s.id === row.school_id)) {
        continue;
      }
    }

    if (selectedRanks !== null) {
      if (selectedRanks.length === 0) {
        if (row.rank !== null) continue;
      } else if (!selectedRanks.find((r) => r.id === String(row.rank))) {
        continue;
      }
    }

    let count = 0;
    if (row === undefined) continue;
    if (row.count === null) continue;
    if (typeof row.count === "number") {
      count = row.count;
    } else {
      count = parseInt(row.count);
    }

    if (!gradeData.has(row.grade_config_id)) {
      gradeData.set(row.grade_config_id, {
        label: row.label,
        count: 0,
      });
    }
    const current = gradeData.get(row.grade_config_id);
    if (current) {
      current.count += count;
    }
  }

  const gradesWithCounts = Array.from(gradeData.entries()).map(
    ([gradeId, data]) => ({
      gradeId,
      label: data.label,
      order:
        formData.data.find((r) => r.grade_config_id === gradeId)?.order ?? 0,
      count: data.count,
    })
  );

  return gradesWithCounts
    .sort((a, b) => a.order - b.order)
    .map((grade) => ({
      name: grade.label,
      value: grade.count,
    }));
}
